import { ref, computed, watch, nextTick } from 'vue'
import servoList from '../servoList.json'
import { shouldSwapAnglesForTrajectory } from '../dualArmTrajectory.js'
import { getCurveValue, fitCubicWithLeastSquares } from '../utils/cubicCurve.js'
import { calculateInverseKinematics, calculateRelativeAngle } from '../utils/kinematics.js'
import {
  isNestedTrajectory,
  findPersonIdByPersonName,
  getOriginPosition,
  resolveMouthCornerServoIdsForDevice
} from '../utils/trajectoryConfig.js'
import { useMotionParamSync } from './useMotionParamSync.js'

// 轨迹管理：trajectoryConfigs 状态、轨迹的增删/选择、轨迹点与角度片段的增删改、
// 双舵机机械臂视图（dualServos/dualArmConfig/trajectoryX/Y）联动
// 外部依赖：
//   trajectoryListData           - 轨迹列表数据 Ref（trajectoryList.json）
//   deviceId                     - 当前设备ID Ref
//   getSelectedDeviceServoVersion- 获取当前设备舵机型号的函数
//   isRestoring                  - 还原中标记 Ref（联动 watch 守卫）
//   showSnackbar                 - 显示提示信息的回调 (msg: string) => void
export function useTrajectoryManager(deps) {
  const { trajectoryListData, deviceId, getSelectedDeviceServoVersion, isRestoring, showSnackbar } = deps

  // ---------- 状态 ----------
  const trajectoryConfigs = ref([]) // [{ id, name, config, servos, armConfig, trajectoryX, trajectoryY, trajectoryPoints, zeroPositionAngles }]
  const currentTrajectoryId = ref(null)

  const dualServos = ref([]) // 双舵机视图的舵机数据
  const dualArmConfig = ref({ servoA: 0, servoB: 0 }) // 双舵机当前角度
  const trajectoryX = ref(0) // 机械臂末端当前X坐标
  const trajectoryY = ref(0) // 机械臂末端当前Y坐标
  const isUpdatingTrajectoryFromDrag = ref(false) // 标记是否正在从拖动更新轨迹参数
  const selectedTrajectoryConfig = ref(null) // 当前选中的轨迹配置（传给 DualServoArm）
  const showDualServoArm = ref(false) // 是否显示双舵机机械臂视图
  const showDualArmVisualization = ref(false) // 是否显示双舵机可视化（否则显示角度图）
  const showAngleParams = ref(false) // 轨迹卡片中角度参数的显示/隐藏

  // 选择轨迹/人物弹窗
  const selectTrajectoryDialog = ref(false)
  const selectPersonDialog = ref(false)
  const selectedTrajectoryName = ref('')

  // 删除轨迹确认弹窗
  const deleteTrajectoryDialog = ref(false)
  const deleteTrajectoryId = ref(null)
  const deleteTrajectoryName = ref('')

  // 当前轨迹配置
  const currentTrajectory = computed(() => {
    return trajectoryConfigs.value.find(t => t.id === currentTrajectoryId.value)
  })

  // ---------- 联动 watch ----------
  // 双舵机片段参数联动（结束角->下段开始角、start/end 变化重置 1/3、2/3 点）
  useMotionParamSync(dualServos, { isRestoring })

  // dualArmConfig 变化时同步到当前轨迹的 armConfig
  watch(() => dualArmConfig.value, (newConfig) => {
    if (currentTrajectoryId.value && showDualArmVisualization.value) {
      const trajectory = trajectoryConfigs.value.find(t => t.id === currentTrajectoryId.value)
      if (trajectory) {
        trajectory.armConfig.servoA = newConfig.servoA
        trajectory.armConfig.servoB = newConfig.servoB
      }
    }
  }, { deep: true })

  // ---------- 轨迹的添加 / 选择 ----------
  function openAddTrajectoryDialog() {
    if (!deviceId.value) {
      showSnackbar('请先选择设备')
      return
    }
    selectTrajectoryDialog.value = true
  }

  // 获取指定轨迹名称下的人物列表（返回以 personName 为键的对象）
  function getTrajectoryPersons(trajectoryName) {
    if (!trajectoryName || !trajectoryListData.value[trajectoryName]) {
      return {}
    }
    const trajectoryGroup = trajectoryListData.value[trajectoryName]
    if (isNestedTrajectory(trajectoryGroup)) {
      const result = {}
      for (const key in trajectoryGroup) {
        const config = trajectoryGroup[key]
        if (config && config.personName) {
          result[config.personName] = config
        }
      }
      return result
    }
    return {}
  }

  // 选择轨迹名称（第一步）：嵌套结构进入人物选择，旧格式直接选中
  function selectTrajectoryName(name, trajectoryGroup) {
    if (isNestedTrajectory(trajectoryGroup)) {
      selectedTrajectoryName.value = name
      selectTrajectoryDialog.value = false
      selectPersonDialog.value = true
    } else {
      selectTrajectory(name, '', trajectoryGroup)
    }
  }

  // 选择轨迹（最终选择，包含轨迹名称和人物名称）
  function selectTrajectory(trajectoryName, personName, trajectory) {
    const fullName = personName ? `${trajectoryName}-${personName}` : trajectoryName

    const existingIndex = trajectoryConfigs.value.findIndex(t => t.name === fullName)

    if (existingIndex >= 0) {
      // 已存在同名轨迹，切换过去
      currentTrajectoryId.value = trajectoryConfigs.value[existingIndex].id
      showSnackbar(`已切换到轨迹"${fullName}"`)
    } else {
      const newId = Date.now()
      const deviceVer = getSelectedDeviceServoVersion()
      const cornerIds = resolveMouthCornerServoIdsForDevice(trajectoryName, deviceVer)
      const trajectoryMerged = { ...trajectory }
      if (cornerIds && Array.isArray(trajectory.ids) && trajectory.ids.length === 2) {
        trajectoryMerged.ids = cornerIds
        if (deviceVer) {
          trajectoryMerged.servoVersions = [...new Set([deviceVer, ...(trajectory.servoVersions || [])])]
        }
      }
      // 优先从 origin 读取初始坐标，兼容旧的 x, y 字段
      const initialPosition = getOriginPosition(trajectory, 'origin')
      const initialX = initialPosition.x
      const initialY = initialPosition.y

      // 嵌套结构时查找对应的数字键（personId）
      let personId = null
      if (personName && trajectoryListData.value[trajectoryName]) {
        const trajectoryGroup = trajectoryListData.value[trajectoryName]
        if (isNestedTrajectory(trajectoryGroup)) {
          personId = findPersonIdByPersonName(trajectoryGroup, personName)
        }
      }

      const newTrajectory = {
        id: newId,
        name: fullName,
        trajectoryName: trajectoryName,
        personName: personName || '',
        personId: personId || '',
        config: {
          name: fullName,
          trajectoryName: trajectoryName,
          personName: personName || '',
          personId: personId || '',
          ...trajectoryMerged
        },
        servos: [],
        armConfig: { servoA: 0, servoB: 0 },
        trajectoryX: initialX,
        trajectoryY: initialY,
        trajectoryPoints: [{x: initialX, y: initialY}],
        zeroPositionAngles: { servoA: 0, servoB: 0 } // 零位角度（初始位置对应的舵机角度）
      }

      trajectoryConfigs.value.push(newTrajectory)
      currentTrajectoryId.value = newId

      // 计算零位角度（从 origin 零位计算）
      if (trajectory.armLengths && trajectory.servoDistance) {
        const zeroPosition = getOriginPosition(trajectory, 'origin')
        const zeroAngles = calculateInverseKinematics(
          zeroPosition.x,
          zeroPosition.y,
          trajectory.armLengths,
          trajectory.servoDistance
        )
        if (zeroAngles) {
          newTrajectory.zeroPositionAngles.servoA = zeroAngles.servoA
          newTrajectory.zeroPositionAngles.servoB = zeroAngles.servoB

          // 计算初始位置相对于零位的偏移
          const initialAngles = calculateInverseKinematics(
            newTrajectory.trajectoryX,
            newTrajectory.trajectoryY,
            trajectory.armLengths,
            trajectory.servoDistance
          )
          if (initialAngles) {
            newTrajectory.armConfig.servoA = calculateRelativeAngle(initialAngles.servoA, zeroAngles.servoA)
            newTrajectory.armConfig.servoB = calculateRelativeAngle(initialAngles.servoB, zeroAngles.servoB)
          } else {
            newTrajectory.armConfig.servoA = 0
            newTrajectory.armConfig.servoB = 0
          }
        }
      }

      // 初始化舵机数据（ids 已按当前设备型号在 trajectoryMerged 中解析）
      if (trajectoryMerged.ids && trajectoryMerged.ids.length > 0) {
        const servoIds = trajectoryMerged.ids
        const servoVersions = trajectoryMerged.servoVersions || []

        newTrajectory.servos = servoIds.map((id) => {
          let servoInfo = null
          for (const version of servoVersions) {
            if (servoList[version]) {
              servoInfo = servoList[version].find(s => s.id === id)
              if (servoInfo) break
            }
          }

          // 初始只有1个轨迹点，不创建角度片段
          return {
            s: id,
            name: servoInfo ? servoInfo.name : `舵机${id}`,
            range: servoInfo ? servoInfo.range : [-200, 200],
            l: 1,
            m: []
          }
        })
      }

      showSnackbar(`已添加轨迹"${fullName}"`)
    }

    selectTrajectoryDialog.value = false
    selectPersonDialog.value = false
    selectedTrajectoryName.value = ''
    showDualServoArm.value = true
    console.log('选中轨迹:', fullName, trajectory)
  }

  // ---------- 轨迹的删除 ----------
  function openDeleteTrajectoryDialog(trajectoryId, trajectoryName) {
    deleteTrajectoryId.value = trajectoryId
    deleteTrajectoryName.value = trajectoryName
    deleteTrajectoryDialog.value = true
  }

  function confirmDeleteTrajectory() {
    if (!deleteTrajectoryId.value) {
      deleteTrajectoryDialog.value = false
      return
    }
    removeTrajectory(deleteTrajectoryId.value)
    deleteTrajectoryDialog.value = false
    deleteTrajectoryId.value = null
    deleteTrajectoryName.value = ''
    showSnackbar('已成功删除轨迹配置')
  }

  function removeTrajectory(trajectoryId) {
    const index = trajectoryConfigs.value.findIndex(t => t.id === trajectoryId)
    if (index >= 0) {
      trajectoryConfigs.value.splice(index, 1)
      if (currentTrajectoryId.value === trajectoryId) {
        currentTrajectoryId.value = trajectoryConfigs.value.length > 0 ? trajectoryConfigs.value[0].id : null
      }
    }
  }

  // ---------- DualServoArm 事件处理 ----------
  function handleTrajectorySaved(trajectoryData) {
    console.log('轨迹已保存:', trajectoryData)
    const dataToSave = {
      [trajectoryData.name]: trajectoryData.data
    }
    console.log('轨迹数据（可保存到trajectoryList.json）:', JSON.stringify(dataToSave, null, 2))
    showSnackbar(`轨迹"${trajectoryData.name}"已保存，请查看控制台`)
  }

  function exitDualServoArm() {
    showDualServoArm.value = false
    selectedTrajectoryConfig.value = null
    dualServos.value = []
  }

  function handleDualServosChanged(newServos) {
    const isInitializing = dualServos.value.length === 0
    dualServos.value = newServos
    // 只在初始化时重置轨迹参数，后续修改片段参数时不重置
    if (isInitializing && selectedTrajectoryConfig.value) {
      trajectoryX.value = Number((selectedTrajectoryConfig.value.x ?? 0).toFixed(1))
      trajectoryY.value = Number((selectedTrajectoryConfig.value.y ?? 0).toFixed(1))
    }
  }

  function handleDualArmConfigChanged(newConfig) {
    dualArmConfig.value = newConfig
    if (currentTrajectory.value) {
      currentTrajectory.value.armConfig = { ...newConfig }
    }
  }

  function handleEndPositionChanged(newPos) {
    // 避免循环更新
    if (!isUpdatingTrajectoryFromDrag.value) {
      isUpdatingTrajectoryFromDrag.value = true
      trajectoryX.value = Number(newPos.x.toFixed(1))
      trajectoryY.value = Number(newPos.y.toFixed(1))

      if (currentTrajectory.value) {
        currentTrajectory.value.trajectoryX = trajectoryX.value
        currentTrajectory.value.trajectoryY = trajectoryY.value
      }

      nextTick(() => {
        isUpdatingTrajectoryFromDrag.value = false
      })
    }
  }

  // 设置双舵机的当前角度（把机械臂当前角度写入指定片段的结束角度）
  function setCurrentAngleForDualServo(idx, mIdx) {
    if (idx >= 0 && dualServos.value[idx]) {
      const servo = dualServos.value[idx]
      const trajectory = currentTrajectory.value

      if (dualServos.value.length === 2) {
        const otherIdx = idx === 0 ? 1 : 0
        const otherServo = dualServos.value[otherIdx]

        const currentAngleA = dualArmConfig.value.servoA
        const currentAngleB = dualArmConfig.value.servoB

        if (servo.m[mIdx]) {
          const angle = trajectory ? getAngleForServoIndex(trajectory, idx, { servoA: currentAngleA, servoB: currentAngleB }) : (idx === 0 ? currentAngleA : currentAngleB)
          servo.m[mIdx].param[1] = Number(angle.toFixed(1))
          // 上一个片段的结束角度是下一个片段的开始角度
          if (servo.m[mIdx + 1]) {
            servo.m[mIdx + 1].param[0] = Number(angle.toFixed(1))
          }
        }

        if (otherServo.m[mIdx]) {
          const otherAngle = trajectory ? getAngleForServoIndex(trajectory, otherIdx, { servoA: currentAngleA, servoB: currentAngleB }) : (otherIdx === 0 ? currentAngleA : currentAngleB)
          otherServo.m[mIdx].param[1] = Number(otherAngle.toFixed(1))
          if (otherServo.m[mIdx + 1]) {
            otherServo.m[mIdx + 1].param[0] = Number(otherAngle.toFixed(1))
          }
        }
      } else {
        const currentAngle = trajectory ? getAngleForServoIndex(trajectory, idx, { servoA: dualArmConfig.value.servoA, servoB: dualArmConfig.value.servoB }) : (idx === 0 ? dualArmConfig.value.servoA : dualArmConfig.value.servoB)

        if (servo.m[mIdx]) {
          servo.m[mIdx].param[1] = Number(currentAngle.toFixed(1))
          if (servo.m[mIdx + 1]) {
            servo.m[mIdx + 1].param[0] = Number(currentAngle.toFixed(1))
          }
        }
      }
    }
  }

  // ---------- 角度换算辅助 ----------
  // 根据轨迹和舵机索引获取对应的角度（右嘴角等场景 A/B 交换）
  function getAngleForServoIndex(trajectory, idx, angles) {
    const shouldSwap = shouldSwapAnglesForTrajectory(trajectory)
    if (shouldSwap) {
      return idx === 0 ? angles.servoB : angles.servoA
    } else {
      return idx === 0 ? angles.servoA : angles.servoB
    }
  }

  // 根据轨迹和舵机索引获取对应的零位角度
  function getZeroAngleForServoIndex(trajectory, idx, zeroAngles) {
    const shouldSwap = shouldSwapAnglesForTrajectory(trajectory)
    if (shouldSwap) {
      return idx === 0 ? zeroAngles.servoB : zeroAngles.servoA
    } else {
      return idx === 0 ? zeroAngles.servoA : zeroAngles.servoB
    }
  }

  // 在起点/终点之间取样并做最小二乘三次拟合，返回 1/3、2/3 点的y值（失败时退化为直线插值）
  function fitThirdPoints(trajectory, servoIdx, startPoint, endPoint, startAngle, endAngle, steps) {
    if (trajectory.config.armLengths && trajectory.config.servoDistance) {
      const numPoints = 10
      const points = []

      for (let i = 0; i <= numPoints; i++) {
        const t = i / numPoints
        const x = startPoint.x + (endPoint.x - startPoint.x) * t
        const y = startPoint.y + (endPoint.y - startPoint.y) * t

        const angles = calculateInverseKinematics(
          x, y,
          trajectory.config.armLengths,
          trajectory.config.servoDistance
        )

        if (angles && trajectory.zeroPositionAngles) {
          const absoluteAngle = getAngleForServoIndex(trajectory, servoIdx, angles)
          const zeroAngle = getZeroAngleForServoIndex(trajectory, servoIdx, trajectory.zeroPositionAngles)
          const angle = calculateRelativeAngle(absoluteAngle, zeroAngle)
          const step = (i * steps) / numPoints
          points.push({ step, angle })
        }
      }

      if (points.length >= 4) {
        const coeffs = fitCubicWithLeastSquares(points, steps)
        if (coeffs) {
          const val1 = getCurveValue(coeffs, steps / 3)
          const val2 = getCurveValue(coeffs, 2 * steps / 3)
          if (!isNaN(val1) && !isNaN(val2) && isFinite(val1) && isFinite(val2)) {
            return [Number(val1.toFixed(1)), Number(val2.toFixed(1))]
          }
        }
      }
    }
    // 退化为直线插值
    return [
      Number((startAngle + (endAngle - startAngle) / 3).toFixed(1)),
      Number((startAngle + 2 * (endAngle - startAngle) / 3).toFixed(1))
    ]
  }

  // 计算某个坐标点相对零位的两臂角度，失败时返回 fallback
  function relativeAnglesAtPoint(trajectory, x, y, fallbackA = 0, fallbackB = 0) {
    let a = fallbackA, b = fallbackB
    if (trajectory.config.armLengths && trajectory.config.servoDistance) {
      const angles = calculateInverseKinematics(x, y, trajectory.config.armLengths, trajectory.config.servoDistance)
      if (angles && trajectory.zeroPositionAngles) {
        a = calculateRelativeAngle(angles.servoA, trajectory.zeroPositionAngles.servoA)
        b = calculateRelativeAngle(angles.servoB, trajectory.zeroPositionAngles.servoB)
      }
    }
    return { servoA: a, servoB: b }
  }

  // ---------- 轨迹点 / 角度片段 ----------
  // 为轨迹点添加舵机角度片段（镜像/返程还原时使用，可指定步数）
  function addServoMotionForTrajectoryPoint(trajectory, pointIdx, specifiedSteps = null) {
    const points = trajectory.trajectoryPoints
    if (!points || pointIdx < 1 || pointIdx >= points.length) return

    const startPoint = points[pointIdx - 1]
    const endPoint = points[pointIdx]

    if (trajectory.servos && trajectory.servos.length > 0) {
      trajectory.servos.forEach((servo, idx) => {
        let motion = null

        if (trajectory.config.armLengths && trajectory.config.servoDistance) {
          const startAngles = relativeAnglesAtPoint(trajectory, startPoint.x, startPoint.y)
          const endAngles = relativeAnglesAtPoint(trajectory, endPoint.x, endPoint.y)

          const startAngle = getAngleForServoIndex(trajectory, idx, startAngles)
          const endAngle = getAngleForServoIndex(trajectory, idx, endAngles)

          // 如果指定了步数则使用之，否则沿用最后一个片段的步数（无片段时默认50）
          let steps = specifiedSteps
          if (steps === null) {
            steps = servo.m.length > 0 ? servo.m[servo.m.length - 1].steps : 50
          }

          const thirdPoint = Number((startAngle + (endAngle - startAngle) / 3).toFixed(1))
          const twoThirdPoint = Number((startAngle + 2 * (endAngle - startAngle) / 3).toFixed(1))

          motion = {
            steps: steps,
            param: [startAngle, endAngle, thirdPoint, twoThirdPoint]
          }
        }

        if (motion) {
          servo.m.push(motion)
        }
      })
    }
  }

  // 添加运动片段（复制末尾轨迹点并为所有舵机追加片段）
  function addMotionForTrajectory(trajectoryId, servoIdx) {
    const trajectory = trajectoryConfigs.value.find(t => t.id === trajectoryId)
    if (trajectory && trajectory.servos[servoIdx]) {
      if (!trajectory.trajectoryPoints) {
        trajectory.trajectoryPoints = [{x: trajectory.trajectoryX, y: trajectory.trajectoryY}]
      }
      const lastPoint = trajectory.trajectoryPoints[trajectory.trajectoryPoints.length - 1]
      const newPoint = {x: lastPoint.x, y: lastPoint.y}
      trajectory.trajectoryPoints.push(newPoint)

      const startAngles = relativeAnglesAtPoint(trajectory, lastPoint.x, lastPoint.y, trajectory.armConfig.servoA, trajectory.armConfig.servoB)
      const endAngles = relativeAnglesAtPoint(trajectory, newPoint.x, newPoint.y, trajectory.armConfig.servoA, trajectory.armConfig.servoB)

      trajectory.servos.forEach((servo, idx) => {
        const mArr = servo.m
        const lastMotion = mArr.length > 0 && mArr[mArr.length - 1] ? mArr[mArr.length - 1] : null
        const startAngle = lastMotion ? lastMotion.param[1] :
          getAngleForServoIndex(trajectory, idx, startAngles)
        const endAngle = getAngleForServoIndex(trajectory, idx, endAngles)

        const [thirdPoint, twoThirdPoint] = fitThirdPoints(trajectory, idx, lastPoint, newPoint, startAngle, endAngle, 10)

        mArr.push({steps: 10, param: [startAngle, endAngle, thirdPoint, twoThirdPoint]})
      })
    }
  }

  // 删除运动片段（成对删除，并移除对应轨迹点）
  function removeMotionForTrajectory(trajectoryId, servoIdx, motionIdx) {
    const trajectory = trajectoryConfigs.value.find(t => t.id === trajectoryId)
    if (trajectory && trajectory.servos[servoIdx]) {
      if (trajectory.servos[servoIdx].m.length <= 1) {
        showSnackbar('至少需要保留一个角度片段')
        return
      }

      trajectory.servos[servoIdx].m.splice(motionIdx, 1)

      // 成对删除
      if (trajectory.servos.length === 2) {
        const otherIdx = servoIdx === 0 ? 1 : 0
        if (trajectory.servos[otherIdx].m.length > motionIdx) {
          trajectory.servos[otherIdx].m.splice(motionIdx, 1)
        }
      }

      // 删除对应的轨迹点（角度片段 motionIdx 连接点 motionIdx 和 motionIdx+1）
      if (trajectory.trajectoryPoints && trajectory.trajectoryPoints.length > motionIdx + 1) {
        trajectory.trajectoryPoints.splice(motionIdx + 1, 1)
      }
    }
  }

  // 添加轨迹点（复制末尾点，并为所有舵机追加对应角度片段）
  function addTrajectoryPoint(trajectoryId) {
    const trajectory = trajectoryConfigs.value.find(t => t.id === trajectoryId)
    if (!trajectory) return

    if (!trajectory.trajectoryPoints) {
      trajectory.trajectoryPoints = [{x: trajectory.trajectoryX, y: trajectory.trajectoryY}]
    }

    const lastPoint = trajectory.trajectoryPoints[trajectory.trajectoryPoints.length - 1]
    const newPoint = {x: lastPoint.x, y: lastPoint.y}
    trajectory.trajectoryPoints.push(newPoint)

    const startAngles = relativeAnglesAtPoint(trajectory, lastPoint.x, lastPoint.y)
    const endAngles = relativeAnglesAtPoint(trajectory, newPoint.x, newPoint.y)

    if (trajectory.servos && trajectory.servos.length > 0) {
      trajectory.servos.forEach((servo, idx) => {
        const mArr = servo.m
        // 开始角度：有前一个片段则用其结束角度，否则用起点角度
        const lastMotion = mArr.length > 0 && mArr[mArr.length - 1] ? mArr[mArr.length - 1] : null
        const startAngle = lastMotion ? lastMotion.param[1] :
          (idx === 0 ? startAngles.servoA : startAngles.servoB)
        const endAngle = idx === 0 ? endAngles.servoA : endAngles.servoB

        const [thirdPoint, twoThirdPoint] = fitThirdPoints(trajectory, idx, lastPoint, newPoint, startAngle, endAngle, 10)

        mArr.push({steps: 10, param: [startAngle, endAngle, thirdPoint, twoThirdPoint]})
      })
    }
  }

  // 删除轨迹点（并删除对应的角度片段）
  function removeTrajectoryPoint(trajectoryId, pointIdx) {
    const trajectory = trajectoryConfigs.value.find(t => t.id === trajectoryId)
    if (!trajectory || !trajectory.trajectoryPoints) return

    if (trajectory.trajectoryPoints.length <= 1) {
      showSnackbar('至少需要保留一个轨迹点')
      return
    }

    trajectory.trajectoryPoints.splice(pointIdx, 1)

    // 删除第N个点对应删除第N-1个角度片段（第0个点对应第0个片段）
    if (trajectory.servos && trajectory.servos.length > 0) {
      const motionIdxToDelete = pointIdx > 0 ? pointIdx - 1 : 0
      trajectory.servos.forEach(servo => {
        if (servo.m.length > motionIdxToDelete) {
          servo.m.splice(motionIdxToDelete, 1)
        }
      })
    }
  }

  // 获取轨迹点对应的步数（第0个点是起始点，无对应片段）
  function getTrajectoryPointSteps(trajectory, pointIdx) {
    if (pointIdx === 0) return 0
    const motionIdx = pointIdx - 1
    if (trajectory.servos && trajectory.servos.length > 0 && trajectory.servos[0].m && trajectory.servos[0].m[motionIdx]) {
      return trajectory.servos[0].m[motionIdx].steps
    }
    return 0
  }

  // 更新轨迹点对应的步数（同步所有舵机）
  function updateTrajectoryPointSteps(trajectory, pointIdx, newSteps) {
    if (pointIdx === 0) return
    const motionIdx = pointIdx - 1
    if (trajectory.servos) {
      trajectory.servos.forEach(servo => {
        if (servo.m && servo.m[motionIdx]) {
          servo.m[motionIdx].steps = Number(newSteps)
        }
      })
    }
  }

  // 更新轨迹配置的循环次数（同步所有舵机）
  function updateTrajectoryLoopCount(trajectory, newLoopCount) {
    const loopCount = Number(newLoopCount) || 1
    if (trajectory.servos && trajectory.servos.length > 0) {
      trajectory.servos.forEach(servo => {
        servo.l = loopCount
      })
    }
  }

  // 移动机械臂到指定轨迹点坐标（切换当前轨迹并进入可视化视图）
  function moveArmToPosition(trajectoryId, pointIdx) {
    const trajectory = trajectoryConfigs.value.find(t => t.id === trajectoryId)
    if (!trajectory || !trajectory.trajectoryPoints) return
    if (pointIdx >= trajectory.trajectoryPoints.length) return

    const point = trajectory.trajectoryPoints[pointIdx]
    if (!point) return

    currentTrajectoryId.value = trajectoryId
    showDualArmVisualization.value = true

    trajectoryX.value = point.x
    trajectoryY.value = point.y

    // 计算舵机角度
    if (trajectory.config.armLengths && trajectory.config.servoDistance) {
      const angles = calculateInverseKinematics(
        point.x,
        point.y,
        trajectory.config.armLengths,
        trajectory.config.servoDistance
      )

      if (angles && trajectory.zeroPositionAngles) {
        dualArmConfig.value.servoA = angles.servoA
        dualArmConfig.value.servoB = angles.servoB
        trajectory.armConfig.servoA = angles.servoA
        trajectory.armConfig.servoB = angles.servoB
      }
    }

    // 切换到当前轨迹的数据
    selectedTrajectoryConfig.value = trajectory.config
    if (trajectory.servos.length > 0) {
      dualServos.value = trajectory.servos
    }
  }

  // 设置当前轨迹点参数（把机械臂当前坐标写入轨迹点，并联动更新角度片段）
  function setCurrentPointForTrajectory(trajectoryId, pointIdx) {
    const trajectory = trajectoryConfigs.value.find(t => t.id === trajectoryId)
    if (!trajectory || !trajectory.trajectoryPoints) return
    if (pointIdx >= trajectory.trajectoryPoints.length) return

    // 使用当前机械臂的x、y坐标
    const currentX = trajectoryX.value || trajectory.trajectoryX
    const currentY = trajectoryY.value || trajectory.trajectoryY

    // 更新轨迹点（创建新对象以确保响应式更新）
    trajectory.trajectoryPoints[pointIdx] = {
      x: Number(currentX.toFixed(1)),
      y: Number(currentY.toFixed(1))
    }

    applyPointAnglesToMotions(trajectory, pointIdx, currentX, currentY)
  }

  // 更新轨迹点（输入框修改坐标后重算对应片段角度）
  function updateTrajectoryPointForTrajectory(trajectory, pointIdx) {
    const point = trajectory.trajectoryPoints[pointIdx]
    if (!point) return

    // 创建新对象以确保响应式更新
    trajectory.trajectoryPoints[pointIdx] = {
      x: point.x,
      y: point.y
    }

    applyPointAnglesToMotions(trajectory, pointIdx, point.x, point.y)
  }

  // 根据坐标计算角度并更新到对应的角度片段：
  // 第0个点更新第0个片段的开始角度；第N个点更新第N-1个片段的结束角度和第N个片段的开始角度
  function applyPointAnglesToMotions(trajectory, pointIdx, x, y) {
    if (!trajectory.config.armLengths || !trajectory.config.servoDistance) return

    const angles = calculateInverseKinematics(
      x, y,
      trajectory.config.armLengths,
      trajectory.config.servoDistance
    )

    if (angles && trajectory.servos && trajectory.servos.length >= 2 && trajectory.zeroPositionAngles) {
      trajectory.servos.forEach((servo, idx) => {
        const absoluteAngle = getAngleForServoIndex(trajectory, idx, angles)
        const zeroAngle = getZeroAngleForServoIndex(trajectory, idx, trajectory.zeroPositionAngles)
        const angle = calculateRelativeAngle(absoluteAngle, zeroAngle)

        if (pointIdx === 0) {
          if (servo.m[0]) {
            servo.m[0].param[0] = Number(angle.toFixed(1))
          }
        } else {
          if (servo.m[pointIdx - 1]) {
            servo.m[pointIdx - 1].param[1] = Number(angle.toFixed(1))
          }
          if (servo.m[pointIdx]) {
            servo.m[pointIdx].param[0] = Number(angle.toFixed(1))
          }
        }
      })
    }
  }

  // 设置当前角度（把机械臂当前角度写入指定片段结束角度，并更新对应轨迹点坐标）
  function setCurrentAngleForTrajectory(trajectoryId, servoIdx, motionIdx) {
    const trajectory = trajectoryConfigs.value.find(t => t.id === trajectoryId)
    if (!trajectory || !trajectory.servos[servoIdx]) return

    const servo = trajectory.servos[servoIdx]

    // 当前轨迹用实时 dualArmConfig，否则用轨迹保存的 armConfig
    const useCurrentAngle = trajectoryId === currentTrajectoryId.value
    const angleA = useCurrentAngle ? dualArmConfig.value.servoA : trajectory.armConfig.servoA
    const angleB = useCurrentAngle ? dualArmConfig.value.servoB : trajectory.armConfig.servoB

    const currentX = trajectoryX.value || trajectory.trajectoryX
    const currentY = trajectoryY.value || trajectory.trajectoryY

    // motionIdx 对应的结束点是 trajectoryPoints[motionIdx + 1]
    if (trajectory.trajectoryPoints && trajectory.trajectoryPoints.length > motionIdx + 1) {
      trajectory.trajectoryPoints[motionIdx + 1] = {
        x: Number(currentX.toFixed(1)),
        y: Number(currentY.toFixed(1))
      }
    }

    const writeAngle = (targetServo, idx) => {
      if (targetServo.m[motionIdx]) {
        const angle = getAngleForServoIndex(trajectory, idx, { servoA: angleA, servoB: angleB })
        targetServo.m[motionIdx].param[1] = Number(angle.toFixed(1))
        if (targetServo.m[motionIdx + 1]) {
          targetServo.m[motionIdx + 1].param[0] = Number(angle.toFixed(1))
        }
      }
    }

    if (trajectory.servos.length === 2) {
      writeAngle(servo, servoIdx)
      const otherIdx = servoIdx === 0 ? 1 : 0
      writeAngle(trajectory.servos[otherIdx], otherIdx)
    } else {
      writeAngle(servo, servoIdx)
    }

    // 如果是当前轨迹，同步更新到armConfig
    if (useCurrentAngle) {
      trajectory.armConfig.servoA = angleA
      trajectory.armConfig.servoB = angleB
    }
  }

  // 回到零位位置（只重置显示坐标和armConfig，不修改已设置的轨迹点和角度片段）
  function resetToInitialPositionForTrajectory(trajectory) {
    if (trajectory.config) {
      const position = getOriginPosition(trajectory.config, 'origin')

      trajectory.trajectoryX = position.x
      trajectory.trajectoryY = position.y

      trajectory.armConfig.servoA = 0
      trajectory.armConfig.servoB = 0

      // 如果是当前轨迹且正在查看可视化，同步更新全局变量
      if (trajectory.id === currentTrajectoryId.value && showDualArmVisualization.value) {
        trajectoryX.value = trajectory.trajectoryX
        trajectoryY.value = trajectory.trajectoryY
        dualArmConfig.value = { servoA: 0, servoB: 0 }
      }
    }
  }

  // 查看轨迹机械臂可视化
  function viewTrajectoryArmVisualization(trajectoryId) {
    currentTrajectoryId.value = trajectoryId
    if (currentTrajectory.value && currentTrajectory.value.servos.length > 0) {
      dualServos.value = currentTrajectory.value.servos
      dualArmConfig.value = currentTrajectory.value.armConfig
      trajectoryX.value = currentTrajectory.value.trajectoryX
      trajectoryY.value = currentTrajectory.value.trajectoryY
      selectedTrajectoryConfig.value = currentTrajectory.value.config
      showDualArmVisualization.value = true
    } else {
      showSnackbar('请先添加轨迹配置')
    }
  }

  return {
    // 状态
    trajectoryConfigs,
    currentTrajectoryId,
    currentTrajectory,
    dualServos,
    dualArmConfig,
    trajectoryX,
    trajectoryY,
    selectedTrajectoryConfig,
    showDualServoArm,
    showDualArmVisualization,
    showAngleParams,
    // 弹窗状态
    selectTrajectoryDialog,
    selectPersonDialog,
    selectedTrajectoryName,
    deleteTrajectoryDialog,
    deleteTrajectoryName,
    // 轨迹增删/选择
    openAddTrajectoryDialog,
    getTrajectoryPersons,
    selectTrajectoryName,
    selectTrajectory,
    openDeleteTrajectoryDialog,
    confirmDeleteTrajectory,
    removeTrajectory,
    // DualServoArm 事件
    handleTrajectorySaved,
    exitDualServoArm,
    handleDualServosChanged,
    handleDualArmConfigChanged,
    handleEndPositionChanged,
    setCurrentAngleForDualServo,
    // 角度换算
    getAngleForServoIndex,
    getZeroAngleForServoIndex,
    // 轨迹点/角度片段
    addServoMotionForTrajectoryPoint,
    addMotionForTrajectory,
    removeMotionForTrajectory,
    addTrajectoryPoint,
    removeTrajectoryPoint,
    getTrajectoryPointSteps,
    updateTrajectoryPointSteps,
    updateTrajectoryLoopCount,
    moveArmToPosition,
    setCurrentPointForTrajectory,
    updateTrajectoryPointForTrajectory,
    setCurrentAngleForTrajectory,
    resetToInitialPositionForTrajectory,
    viewTrajectoryArmVisualization
  }
}
